// src/layouts/Layout.jsx — FINAL (adds Attendance external link, no breaking changes)
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useTheme } from '../contexts/ThemeContext.jsx';
import {
  LayoutDashboard,
  Calendar,
  BookOpen,
  Users,
  Building,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  CalendarCheck,
  Sun,
  Moon,
  UploadCloud,
} from 'lucide-react';
import LogoMark from '../components/LogoMark.jsx';
import { BOOKING_BASE } from '../constants.js';

function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDay = theme === 'day';
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  useEffect(() => {
    const root = document.documentElement;
    if (sidebarOpen) root.classList.add('sidebar-open');
    else root.classList.remove('sidebar-open');
    return () => root.classList.remove('sidebar-open');
  }, [sidebarOpen]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Main navigation (non-admin)
  const navigation = [
    { name: 'Dashboard', to: BOOKING_BASE, icon: LayoutDashboard },
    { name: 'Booking', to: `${BOOKING_BASE}/booking`, icon: Calendar },
    { name: 'My Bookings', to: `${BOOKING_BASE}/my-bookings`, icon: BookOpen },
    {
      name: 'AttendanceApp',
      href: 'https://attendance.24slides.dev/',
      icon: CalendarCheck,
      external: true,
    },
  ];

  const adminNavigation = [
    { name: 'Manage Users', to: `${BOOKING_BASE}/admin/users`, icon: Users },
    { name: 'Manage Resources', to: `${BOOKING_BASE}/admin/resources`, icon: Building },
    { name: 'Booking & Reports', to: `${BOOKING_BASE}/admin/bookings`, icon: FileText },
    { name: 'Integrations', to: `${BOOKING_BASE}/admin/integrations`, icon: Settings },
    { name: 'Seed Desks', to: `${BOOKING_BASE}/admin/seed-desks`, icon: UploadCloud },
  ];

  const isActiveTo = (to) => {
    if (!to) return false;
    const p = location.pathname.replace(/\/$/, '') || '/';
    const t = to.replace(/\/$/, '') || '/';
    if (t === BOOKING_BASE.replace(/\/$/, '')) return p === t;
    return p === t || p.startsWith(`${t}/`);
  };

  const NavItem = ({ item, onClick }) => {
    const active = !item.external && isActiveTo(item.to);

    const baseClass =
      `group flex items-center px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
        active
          ? 'bg-gradient-to-r from-sky-500/90 to-emerald-500/90 text-white shadow-[0_10px_30px_-14px_rgba(14,165,233,0.7)]'
          : 'text-gray-300 hover:bg-white/10 hover:text-white'
      } ${active ? 'nav-item-active' : 'nav-item-inactive'} nav-item`;

    const iconClass =
      `mr-3 flex-shrink-0 h-5 w-5 transition-colors ${
        active ? 'text-white' : 'text-gray-400 group-hover:text-white'
      } nav-icon`;

    // External link: use <a> so it opens a new tab and does not touch router state
    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
          className={baseClass}
        >
          <item.icon className={iconClass} />
          <span className="flex-1">{item.name}</span>
          <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-white" />
        </a>
      );
    }

    // Internal link
    return (
      <Link to={item.to} onClick={onClick} className={baseClass}>
        <item.icon className={iconClass} />
        {item.name}
      </Link>
    );
  };

  const pageTitle =
    navigation.find((i) => !i.external && isActiveTo(i.to))?.name ||
    adminNavigation.find((i) => isActiveTo(i.to))?.name ||
    'Dashboard';

  return (
    <div className="app-shell min-h-screen bg-gray-900 text-gray-100">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full w-64 bg-gray-900/60 border-r border-white/10 backdrop-blur-xl shadow-2xl transform transition-transform duration-300 -translate-x-full ${
          sidebarExpanded ? 'lg:translate-x-0' : 'lg:-translate-x-full'
        } sidebar-shell`}
        style={{ transform: sidebarOpen ? 'translateX(0)' : undefined }}
      >
        {/* Brand / Close */}
        <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-sky-500 to-emerald-500">
          <div className="flex items-center">
            <LogoMark className="w-14 h-14 mr-3" />
            <span className="text-white font-semibold text-lg tracking-wide">Spacebook</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:bg-white/10 p-1 rounded-lg"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex h-[calc(100%-4rem)] flex-col px-3 py-4">
          <div className="space-y-1">
            {navigation.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                onClick={() => setSidebarOpen(false)}
              />
            ))}
          </div>

          <div className="mt-6 px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Admin
          </div>
          <div className="space-y-1">
            {adminNavigation.map((item) => (
              <NavItem
                key={item.name}
                item={item}
                onClick={() => setSidebarOpen(false)}
              />
            ))}
          </div>

          <div className="mt-auto pt-4">
            <div className="flex items-center mb-3">
              <div className="w-9 h-9 bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-sm font-medium">
                  {user?.username?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{user?.username}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="group flex items-center w-full px-3 py-2 text-sm font-medium text-gray-300 rounded-xl hover:bg-red-600 hover:text-white transition-all duration-200"
            >
              <LogOut className="mr-3 h-4 w-4" />
              Sign out
            </button>
          </div>
        </nav>
      </aside>

      {/* Backdrop mobile */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className={`relative ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-0'}`}>
        <header className="sticky top-0 z-30 bg-gray-900/60 backdrop-blur-xl border-b border-white/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-300 hover:text-white"
                aria-label="Open sidebar"
              >
                <Menu className="h-6 w-6" />
              </button>
              <button
                onClick={() => setSidebarExpanded((prev) => !prev)}
                className="hidden lg:inline-flex text-gray-300 hover:text-white"
                aria-label={sidebarExpanded ? 'Hide sidebar' : 'Show sidebar'}
              >
                {sidebarExpanded ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
            <h1 className="text-xl font-semibold text-white tracking-tight">{pageTitle}</h1>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-gray-400">{isDay ? 'Day' : 'Night'}</span>
              <button
                type="button"
                onClick={toggleTheme}
                className="relative inline-flex h-7 w-12 items-center rounded-full border border-white/10 bg-gray-900/40 shadow-inner transition-colors duration-200"
                aria-pressed={isDay}
                aria-label={isDay ? 'Switch to night mode' : 'Switch to day mode'}
              >
                <span
                  className={`inline-flex h-5 w-5 transform items-center justify-center rounded-full shadow transition duration-200 ${isDay ? 'translate-x-6 bg-yellow-100 text-yellow-600' : 'translate-x-1 bg-gray-950 text-sky-300'}`}
                >
                  {isDay ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                </span>
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-gray-900/40 p-6 shadow-[0_30px_80px_-60px_rgba(0,0,0,0.9)]">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;










